import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, doc, getDoc } from "firebase/firestore";
import { FaWhatsapp } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { firestore } from "../firebase-config";
import { id_firebase_loja, id_firebase_estoque } from "../content-site";
import { retiraMascara } from "../utils/retiraMascara";
import { formatCambio } from "../utils/formatCambio";
import { formatMoney } from "../utils/formatMoney";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function VeiculoClient() {
    const { idVeiculo } = useParams();
    const [veiculo, setVeiculo] = useState(null);
    const [revenda, setRevenda] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);

        const carregarDados = async () => {
            try {
                const revendaRef = doc(collection(firestore, "revenda"), id_firebase_loja);
                const revendaSnap = await getDoc(revendaRef);

                if (revendaSnap.exists()) {
                    setRevenda(revendaSnap.data());
                }

                const veiculoRef = doc(
                    collection(doc(collection(firestore, "revenda"), id_firebase_estoque), "veiculos"),
                    idVeiculo
                );
                const veiculoSnap = await getDoc(veiculoRef);

                if (veiculoSnap.exists()) {
                    setVeiculo({ ...veiculoSnap.data(), id: veiculoSnap.id });
                } else {
                    setVeiculo(null);
                }
            } finally {
                setLoading(false);
            }
        };

        carregarDados();
    }, [idVeiculo]);

    const whatsappLink = useMemo(() => {
        if (!revenda?.petelefone2 || !veiculo) return "#";

        const telefone = retiraMascara(revenda.petelefone2, "link");
        const mensagem = `Olá, tenho interesse no ${veiculo.vefipe_marca} - ${veiculo.vefipe_name}.`;

        return `https://api.whatsapp.com/send?phone=55${telefone}&text=${encodeURIComponent(mensagem)}`;
    }, [revenda, veiculo]);

    const imagens = Array.isArray(veiculo?.imagens) ? veiculo.imagens : [];
    const opcionais = Array.isArray(veiculo?.opcionais) ? veiculo.opcionais : [];
    const precoFormatado = "Consulte agora!"

    if (loading) {
        return <div className="text-center py-20">Carregando veículo...</div>;
    }

    if (!veiculo) {
        return (
            <div>
                <Header />
                <div className="min-h-[60vh] pt-28 px-4 flex items-center justify-center">
                    <div className="max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl">
                        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-amber-600">Veículo indisponível</p>
                        <h1 className="mt-3 text-2xl font-bold text-slate-900">Não encontramos este anúncio.</h1>
                        <p className="mt-3 text-slate-600">
                            O veículo pode ter sido removido ou atualizado no estoque.
                        </p>
                        <a
                            href="/estoque"
                            className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                        >
                            Voltar para o estoque
                        </a>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="bg-[radial-gradient(circle_at_top,_rgba(250,204,21,0.16),_transparent_30%),linear-gradient(180deg,_#0f172a_0%,_#f8fafc_22%)]">
            <Header />

            <main className="mx-auto max-w-7xl px-4 pb-12 pt-28 sm:px-6 lg:px-8 mt-12">
                <div className="grid gap-8 lg:grid-cols-[1.65fr_1fr]">
                    <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.14)] backdrop-blur">
                        <div className="relative bg-slate-950">
                            {imagens.length > 0 ? (
                                <Swiper
                                    spaceBetween={10}
                                    slidesPerView={1}
                                    modules={[Navigation]}
                                    navigation={{
                                        nextEl: ".swiper-button-next-custom",
                                        prevEl: ".swiper-button-prev-custom",
                                    }}
                                    className="group"
                                >
                                    {imagens.map((img, index) => (
                                        <SwiperSlide key={index}>
                                            <div className="flex items-center justify-center bg-slate-950">
                                                <img
                                                    src={img.fileUrl}
                                                    alt={`Foto ${index + 1} do veículo`}
                                                    className="h-[320px] w-full object-contain sm:h-[420px] lg:h-[520px]"
                                                />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            ) : (
                                <div className="flex h-[320px] items-center justify-center bg-slate-950 text-sm text-white/70 sm:h-[420px] lg:h-[520px]">
                                    Nenhuma imagem disponível
                                </div>
                            )}

                            {imagens.length > 1 && (
                                <>
                                    <button
                                        className="swiper-button-prev-custom absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-3 text-slate-900 shadow-lg transition hover:bg-white"
                                        aria-label="Foto anterior"
                                    >
                                        <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                                            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                    <button
                                        className="swiper-button-next-custom absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-3 text-slate-900 shadow-lg transition hover:bg-white"
                                        aria-label="Próxima foto"
                                    >
                                        <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                                            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>

                        <div className="space-y-8 p-5 sm:p-8">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-600">Destaque do estoque</p>
                                    <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                                        {veiculo.vefipe_marca} - {veiculo.vefipe_name}
                                    </h1>
                                    <p className="mt-2 text-sm text-slate-500">
                                        {veiculo.vefipe_ano_modelo} • {veiculo.vefipe_combustivel} • {formatCambio(veiculo.vecambio)}
                                    </p>
                                </div>

                                <div className="rounded-2xl bg-amber-400 px-5 py-4 text-slate-950 shadow-lg shadow-amber-400/30">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.28em]">Preço</p>
                                    <p className="mt-1 text-3xl font-black leading-none">{precoFormatado}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                {[
                                    { label: "Ano", value: veiculo.vefipe_ano_modelo },
                                    { label: "Cor", value: veiculo.vecor },
                                    { label: "Câmbio", value: formatCambio(veiculo.vecambio) },
                                    { label: "KM", value: `${veiculo.vequilometragem || "--"} km` },
                                ].map((item) => (
                                    <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{item.label}</p>
                                        <p className="mt-2 text-base font-semibold text-slate-900">{item.value || "--"}</p>
                                    </div>
                                ))}
                            </div>

                            <div>
                                <h2 className="text-lg font-bold text-slate-950">Opcionais</h2>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {opcionais.length > 0 ? (
                                        opcionais.map((item, idx) => (
                                            <span
                                                key={idx}
                                                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm"
                                            >
                                                {typeof item === "object" ? item.descricao : item}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-sm text-slate-500">Nenhum opcional informado.</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    <aside className="lg:sticky lg:top-24 lg:self-start">
                        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-950 text-white shadow-[0_20px_80px_rgba(15,23,42,0.25)]">
                            <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-700 p-6">
                                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/80">Contato direto</p>
                                <h2 className="mt-2 text-2xl font-black">Fale com a revenda no WhatsApp</h2>
                                <p className="mt-3 text-sm text-white/85">
                                    Atendimento rápido para tirar dúvidas, enviar proposta e confirmar disponibilidade.
                                </p>
                            </div>

                            <div className="space-y-5 p-6">
                                <div className="rounded-2xl bg-white/5 p-4">
                                    <p className="text-xs uppercase tracking-[0.24em] text-white/60">Telefone</p>
                                    <p className="mt-2 text-lg font-semibold">
                                        {revenda?.petelefone2 ? retiraMascara(revenda.petelefone2) : "Não informado"}
                                    </p>
                                </div>

                                <a
                                    href={whatsappLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-4 text-base font-black text-emerald-700 transition hover:bg-emerald-50"
                                >
                                    <FaWhatsapp className="text-2xl" />
                                    Chamar no WhatsApp
                                </a>

                                <p className="text-xs leading-relaxed text-white/65">
                                    Mensagem sugerida já vai preenchida com o nome do veículo e o contato é direcionado para
                                    o número principal da revenda.
                                </p>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>

            <Footer />
        </div>
    );
}
